/**
 * SeoComboLanding — high-intent budget × city landing pages for organic
 * traffic. R109.12.
 *
 * Routes wired in App.js:
 *   /business-ideas/:band                        (English national)
 *   /business-ideas/:band/:city                  (English city × band)
 *   ?lang=hi                                     (Hinglish variant)
 *
 * Content flows from the backend `/api/seo/combo` endpoint, which reuses
 * the existing city-match ranking + city-adjusted budget
 * ranges. Weak combos return robots="noindex,follow" to prevent thin
 * pages from being indexed.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/venture/Navbar";
import { SEOHead } from "@/components/venture/SEOHead";
import api from "@/lib/api";
import { optimizeImg, srcSetFor } from "@/lib/imgUrl";
import { MapPin, ArrowRight } from "lucide-react";

const INR = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

function track(event, meta = {}) {
  try {
    const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/_vk/pv";
    const body = JSON.stringify({
      event, path: window.location.pathname + window.location.search,
      session_id: localStorage.getItem("vk_session_id"), meta,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
    }
  } catch (_) { /* noop */ }
}

export default function SeoComboLanding() {
  const { band, city } = useParams();
  const [sp] = useSearchParams();
  const lang = sp.get("lang") === "hi" ? "hi" : "en";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const path = city ? `${band}/${city}` : band;
    api.get(`/seo/combo/${path}?lang=${lang}`)
      .then(({ data }) => { if (!cancelled) { setData(data); track("seo_landing_view", { path, lang }); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [band, city, lang]);

  const finderHref = data?.cta?.primary?.href || "/business-finder";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-5xl mx-auto p-8 text-center text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-5xl mx-auto p-8 text-center text-slate-600">
          Page not found. Try <Link className="text-emerald-700 underline" to="/business-finder">Business Finder</Link>.
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={data.meta.title}
        description={data.meta.description}
        canonical={data.meta.canonical}
        robots={data.meta.robots}
        lang={data.meta.lang}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16">

          {/* Hero */}
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-emerald-700 mb-2">
              {data.hero.eyebrow}
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl font-black text-slate-900 leading-tight">
              {data.hero.h1}
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl">{data.hero.sub}</p>
            <div className="mt-5 flex gap-3">
              <Link to={finderHref}>
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6"
                        data-testid="combo-primary-cta">
                  {data.cta.primary.label} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to={data.cta.secondary.href}>
                <Button variant="outline" className="rounded-full h-11 px-6" data-testid="combo-secondary-cta">
                  {data.cta.secondary.label}
                </Button>
              </Link>
            </div>
          </div>

          {/* Ventures grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data.ventures || []).map((v, i) => (
              <Link key={v.slug} to={`/venture/${v.slug}`} data-testid={`combo-venture-${i}`}
                    onClick={() => track("finder_cta_click", { slug: v.slug, source: "combo" })}>
                <Card className="rounded-2xl border-slate-200 overflow-hidden hover:shadow-md transition-shadow h-full">
                  <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                    {v.image_url && (
                      <img
                        src={optimizeImg(v.image_url, 480)}
                        srcSet={srcSetFor(v.image_url, [320, 480, 640])}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        alt={v.name}
                        width="640"
                        height="360"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">{v.industry}</div>
                    <div className="font-heading text-base font-bold text-slate-900 mt-1 leading-snug">{v.name}</div>
                    {v.investment_range && (
                      <div className="mt-2 text-sm font-semibold text-emerald-800">
                        {INR(v.investment_range.min)} – {INR(v.investment_range.max)}
                      </div>
                    )}
                    {v.city_reason && (
                      <div className="mt-2 text-xs text-slate-600">
                        <MapPin className="inline h-3 w-3 mr-1 text-emerald-700" />
                        {v.city_reason.slice(0, 160)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* FAQs */}
          {(data.faqs || []).length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">FAQs</h2>
              <div className="space-y-3">
                {data.faqs.map((f, i) => (
                  <Card key={i} className="rounded-2xl border-slate-200">
                    <CardContent className="p-5">
                      <div className="font-semibold text-slate-900">{f.q}</div>
                      <div className="mt-1 text-sm text-slate-600">{f.a}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* JSON-LD */}
          {(data.json_ld || []).map((ld, i) => (
            <script key={i} type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
          ))}
        </main>
      </div>
    </>
  );
}
